import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Sparkles, ArrowRight, Zap, ShieldCheck, Bot, LineChart, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * OnboardingModal — 60-second first-run walkthrough.
 * Renders when auth/me returns { onboarded: false }.
 * Three paths: quickstart-bot / guided-tour / skip.
 */
export default function OnboardingModal() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.onboarded === false) {
      // Small delay so the dashboard mounts first
      const t = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(t);
    }
  }, [user]);

  const dismiss = async (silent = false) => {
    setOpen(false);
    try { await api.post("/onboarding/complete"); } catch { /* best-effort */ }
    await refreshUser();
    if (!silent) toast.info("You can always find the tour in Settings later.");
  };

  const doQuickstart = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/quickstart-bot");
      const botName = data?.bot?.name || "Your first bot";
      toast.success(`🚀 ${botName} is running — $5/tick DCA on AAPL, paper mode.`);
      await refreshUser();
      setOpen(false);
      navigate("/bots");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the quickstart bot.");
    } finally {
      setBusy(false);
    }
  };

  const TOUR = [
    {
      icon: LineChart,
      title: "Every chart has a Copilot",
      body: "Tap any stock or crypto and the Chart Copilot reads the setup in plain English — flags, ABCDs, entries, stops. No jargon.",
      cta: "Next",
      onCta: () => setStep(step + 1),
    },
    {
      icon: Bot,
      title: "Bots that trade for you",
      body: "Pick a strategy (DCA, momentum, grid, AI, patterns), a symbol, and Aanar handles fills. Paper mode by default — flip to IBKR 1-tap when you're ready.",
      cta: "Next",
      onCta: () => setStep(step + 1),
    },
    {
      icon: ShieldCheck,
      title: "Behavioral guardrails",
      body: "Aanar catches overtrading and reckless sizing before you fire — with an override log so you learn from the times you pushed past a warning.",
      cta: "Next",
      onCta: () => setStep(step + 1),
    },
    {
      icon: Zap,
      title: "You start with $100k in paper cash",
      body: "Fractional shares from $1 up — buy $5 of AAPL, copy a verified bot's positions, or draft real trades into IBKR when you connect.",
      cta: "Finish tour",
      onCta: () => dismiss(),
    },
  ];

  if (!user || user.onboarded !== false) return null;

  const cur = TOUR[step - 1]; // step 0 = landing screen; 1..4 = tour steps

  return (
    <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : dismiss()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-border" data-testid="onboarding-modal">
        <button onClick={() => dismiss()}
                data-testid="onboarding-close"
                className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-secondary/60 hover:bg-secondary flex items-center justify-center transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>

        {step === 0 ? (
          /* Landing screen — three choices */
          <div className="p-6">
            <DialogHeader className="text-left">
              <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-[hsl(var(--ai))]/10 border border-[hsl(var(--ai))]/30 text-[hsl(var(--ai))] text-[10px] font-mono-tab uppercase tracking-wider w-fit mb-3">
                <Sparkles className="w-3 h-3" /> 60-second setup
              </div>
              <DialogTitle className="font-display text-2xl leading-tight">
                Welcome to Aanar,{" "}
                <span className="text-[hsl(var(--ai))]">{user.full_name?.split(" ")[0] || "trader"}</span>.
              </DialogTitle>
              <DialogDescription className="pt-2 text-sm">
                Your paper account is loaded with <span className="font-mono-tab text-foreground font-semibold">$100,000</span>.
                What do you want to do first?
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 space-y-2">
              <button
                onClick={doQuickstart}
                disabled={busy}
                data-testid="onboarding-quickstart-btn"
                className="group w-full text-left p-4 rounded-xl bg-[hsl(var(--ai))]/10 hover:bg-[hsl(var(--ai))]/15 border border-[hsl(var(--ai))]/40 transition-all disabled:opacity-60">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--ai))]/20 flex items-center justify-center shrink-0">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--ai))]" />
                          : <Bot className="w-5 h-5 text-[hsl(var(--ai))]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-base">Set up my first bot</div>
                      <ArrowRight className="w-4 h-4 text-[hsl(var(--ai))] group-hover:translate-x-0.5 transition-transform" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      We'll launch a DCA bot on AAPL, $5 per tick, paper mode. Running in one click.
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setStep(1)}
                data-testid="onboarding-tour-btn"
                className="group w-full text-left p-4 rounded-xl bg-secondary/40 hover:bg-secondary/60 border border-border transition-all">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <LineChart className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-base">Show me around</div>
                      <ArrowRight className="w-4 h-4 opacity-40 group-hover:opacity-80 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      4 quick screens — Chart Copilot, bots, guardrails, fractional shares.
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => dismiss(true)}
                data-testid="onboarding-skip-btn"
                className="w-full text-xs text-muted-foreground hover:text-foreground py-2 transition-colors">
                Skip · I'll poke around on my own
              </button>
            </div>
          </div>
        ) : (
          /* Tour step */
          <div className="p-6" data-testid={`onboarding-tour-step-${step}`}>
            <DialogHeader className="text-left">
              <div className="flex items-center gap-2 mb-3">
                {TOUR.map((_, i) => (
                  <div key={i}
                       className={`h-1 flex-1 rounded-full transition-colors ${i < step ? "bg-[hsl(var(--ai))]" : "bg-secondary"}`} />
                ))}
              </div>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-[hsl(var(--ai))]/10 border border-[hsl(var(--ai))]/30 flex items-center justify-center shrink-0">
                  <cur.icon className="w-5 h-5 text-[hsl(var(--ai))]" />
                </div>
                <div>
                  <DialogTitle className="font-display text-xl leading-tight">{cur.title}</DialogTitle>
                  <DialogDescription className="pt-1.5 text-sm leading-relaxed">{cur.body}</DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="mt-5 flex items-center gap-2">
              {step > 1 && (
                <Button variant="outline" size="sm"
                        data-testid="onboarding-back-btn"
                        onClick={() => setStep(step - 1)}
                        className="rounded-full">Back</Button>
              )}
              <div className="flex-1" />
              <Button variant="ghost" size="sm"
                      data-testid="onboarding-tour-skip-btn"
                      onClick={() => dismiss(true)}
                      className="rounded-full text-muted-foreground">Skip</Button>
              <Button size="sm"
                      data-testid={`onboarding-next-btn-${step}`}
                      onClick={cur.onCta}
                      className="rounded-full bg-[hsl(var(--ai))] hover:bg-[hsl(var(--ai))]/90 text-background">
                {step === TOUR.length
                  ? (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {cur.cta}</>)
                  : (<>{cur.cta} <ArrowRight className="w-3.5 h-3.5 ml-1" /></>)}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
